// Epics index — renders s.root.epicsTree (from fetchEpicsTree).
// One card per post-B-117 product epic with rolled-up story / task /
// bug counts + a done-% progress bar.

export const TicketsEpicsMain = {
  extends: 'AppMain',

  Header: { extends: 'TicketsHeader' },

  Body: {
    overflow: 'auto',

    Hero: {
      extends: 'PageHero',
      eyebrow: 'ROADMAP · PRODUCT EPICS · 12 OF 13',
      headline: 'Epics',
      lede: (el, s) => {
        const tree = s.root.epicsTree || []
        if (!tree.length) return 'Loading epics from public.tickets…'
        const withContent = tree.filter((e) => e.storiesTotal > 0).length
        const leafCount = tree.reduce((a, e) => a + e.tasksTotal + e.bugsTotal, 0)
        return `${tree.length} product epics · ${withContent} active · rolling up ${leafCount} tasks + bugs. (1 system epic hidden — simona is an AI-assistant role, not a product category.)`
      },

      Stats: {
        flow: 'x',
        gap: 'B',
        marginTop: 'Z',
        flexWrap: 'wrap',
        childExtends: 'TicketsEpicHeroStat',
        childrenAs: 'state',
        children: (el, s) => {
          const tree = s.root.epicsTree || []
          const tasks = tree.reduce((a, e) => a + e.tasksTotal, 0)
          const bugs = tree.reduce((a, e) => a + e.bugsTotal, 0)
          const total = tasks + bugs
          const done = tree.reduce((a, e) => a + e.tasksDone + e.bugsDone, 0)
          return [
            { label: 'epics (of 13)', color: 'cyan', value: tree.length },
            { label: 'tasks', color: 'magenta', value: tasks },
            { label: 'bugs', color: 'red', value: bugs },
            { label: '% done', color: 'green', value: total ? Math.round((done / total) * 100) : 0 }
          ]
        }
      }
    },

    Grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
      gap: 'A',
      padding: 'C B1 D B1',
      maxWidth: 'L+L',
      width: '100%',
      margin: '0 auto',

      childExtends: 'TicketsEpicCard',
      childrenAs: 'state',
      children: (el, s) => s.root.epicsTree || []
    },

    Empty: {
      hide: (el, s) => (s.root.epicsTree || []).length > 0,
      flow: 'y',
      align: 'center center',
      gap: 'Z1',
      padding: 'D',
      color: 'caption',
      Text: {
        tag: 'p',
        text: 'No epics in public.tickets. Run seed-tickets-from-md.mjs to populate.'
      }
    }
  }
}

// Small hero stat — numeric value + label with a colored accent bar.
export const TicketsEpicHeroStat = {
  flow: 'x',
  gap: 'A',
  align: 'center left',
  paddingLeft: 'A',
  position: 'relative',

  Bar: {
    position: 'absolute',
    top: 'V',
    bottom: 'V',
    left: '0',
    width: '3px',
    round: 'V',
    background: (el, s) => `var(--color-${s.color || 'cyan'})`,
    boxShadow: (el, s) => `0 0 12px color-mix(in srgb, var(--color-${s.color || 'cyan'}) 50%, transparent)`
  },

  Stack: {
    flow: 'y',
    gap: 'V',
    Value: {
      tag: 'span',
      fontSize: 'D',
      fontWeight: '700',
      letterSpacing: '-.02em',
      lineHeight: '1',
      color: 'title',
      text: (el, s) => String(s.value ?? 0)
    },
    Label: {
      tag: 'span',
      fontSize: 'V',
      fontWeight: '700',
      letterSpacing: '.12em',
      color: 'caption',
      text: (el, s) => (s.label || '').toUpperCase()
    }
  }
}

// Individual epic card — title + key, story / task / bug counts,
// progress bar. Click routes to /tickets/general and sets root.filterEpic
// so the triage lists scope to this epic.
export const TicketsEpicCard = {
  tag: 'div',
  flow: 'y',
  gap: 'V',
  padding: 'A',
  background: 'overlay',
  round: 'A',
  borderWidth: 'V',
  borderStyle: 'solid',
  borderColor: 'line',
  cursor: 'pointer',
  transition: 'border-color 0.15s ease, transform 0.15s ease',
  ':hover': { borderColor: 'title', transform: 'translateY(-2px)' },
  onClick: (e, el, s) => {
    const root = s.root || el.getRoot()
    const path = `/tickets/general?epic=${encodeURIComponent(s.epicKey || '')}`
    if (root && typeof root.update === 'function') root.update({ filterEpic: s.epicKey || null })
    // Prefer SPA nav via root.router (el.router on a deep child fails
    // when context isn't fully threaded). Fall back to full nav.
    try {
      if (root && typeof root.router === 'function') {
        const p = root.router(path, root)
        if (p && typeof p.catch === 'function') p.catch(() => { window.location.href = path })
        return
      }
    } catch (_) {}
    if (typeof window !== 'undefined') window.location.href = path
  },

  Head: {
    flow: 'x',
    align: 'center space-between',
    gap: 'V',

    Title: {
      tag: 'span',
      fontSize: 'A',
      fontWeight: '700',
      color: 'title',
      letterSpacing: '-.01em',
      text: (el, s) => s.title || s.epicKey?.toUpperCase() || '?'
    },
    DonePct: {
      tag: 'span',
      flexShrink: '0',
      fontSize: 'Y2',
      fontWeight: '700',
      color: (el, s) => (s.donePct || 0) >= 100 ? 'green' : 'caption',
      text: (el, s) => `${s.donePct || 0}%`
    }
  },

  Key: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    fontWeight: '600',
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    text: (el, s) => s.epicKey || ''
  },

  Counts: {
    flow: 'x',
    gap: 'Z',
    marginTop: 'V',
    flexWrap: 'wrap',

    Stories: {
      hide: (el, s) => !s.storiesTotal,
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => `${s.storiesDone}/${s.storiesTotal} stories`
    },
    Tasks: {
      hide: (el, s) => !s.tasksTotal,
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => `${s.tasksDone}/${s.tasksTotal} tasks`
    },
    Bugs: {
      hide: (el, s) => !s.bugsTotal,
      tag: 'span',
      fontSize: 'Y2',
      color: 'red',
      text: (el, s) => `${s.bugsDone}/${s.bugsTotal} bugs`
    },
    EmptyNote: {
      hide: (el, s) => (s.tasksTotal + s.bugsTotal + s.storiesTotal) > 0,
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      fontStyle: 'italic',
      text: 'no stories yet'
    }
  },

  Progress: {
    hide: (el, s) => !(s.tasksTotal + s.bugsTotal),
    height: '4px',
    round: 'V',
    background: 'line',
    overflow: 'hidden',
    marginTop: 'V',

    Bar: {
      height: '100%',
      round: 'V',
      background: (el, s) => (s.donePct || 0) >= 100 ? 'var(--color-green)' : 'var(--color-cyan)',
      width: (el, s) => `${s.donePct || 0}%`,
      transition: 'width 0.25s ease'
    }
  }
}
